import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Receipt, FileText, CheckCircle, FileLock2 } from "lucide-react";
import type { ApprovedExpense } from "../hooks/useApprovedExpenses";
import {
  formatExpenseCategory,
  formatCurrency,
  parseExpenseDate,
} from "../../utils/expense-schema";
import {
  ExpenseReceiptPreviewDialog,
  type ExpenseReceiptPreview,
} from "../../components/ExpenseReceiptPreviewDialog";
import { SourcePayoutStatus } from "@/features/billing/SourcePayoutStatus";
import { ExpenseOrgNote } from "./ExpenseOrgNote";

interface ExpenseHistoryTableProps {
  expenses: ApprovedExpense[];
  organizationBusinessUnitId: string;
  canEditOrgNotes: boolean;
}

export function ExpenseHistoryTable({
  expenses,
  organizationBusinessUnitId,
  canEditOrgNotes,
}: ExpenseHistoryTableProps) {
  const [previewReceipt, setPreviewReceipt] =
    useState<ExpenseReceiptPreview | null>(null);
  // Group expenses by provider
  const groupedByProvider = useMemo(() => {
    const groups = new Map<
      string,
      {
        provider: { id: string; name: string };
        expenses: ApprovedExpense[];
        totalAmount: number;
      }
    >();

    for (const expense of expenses) {
      const providerName = `${expense.providerFirstName || ""} ${
        expense.providerLastName || ""
      }`.trim();

      if (!groups.has(expense.providerId)) {
        groups.set(expense.providerId, {
          provider: {
            id: expense.providerId,
            name: providerName || "Unknown Provider",
          },
          expenses: [],
          totalAmount: 0,
        });
      }

      const group = groups.get(expense.providerId)!;
      group.expenses.push(expense);
      group.totalAmount += parseFloat(expense.amount);
    }

    return Array.from(groups.values());
  }, [expenses]);

  const grandTotal = useMemo(() => {
    return expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);
  }, [expenses]);

  if (expenses.length === 0) {
    return (
      <Alert>
        <Receipt className="h-4 w-4" />
        <AlertDescription>
          No approved expenses found for the selected period.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-green-50 border border-green-200 rounded-md">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <div>
            <div className="text-sm font-medium text-green-900">
              {expenses.length} Approved Expense
              {expenses.length !== 1 ? "s" : ""}
            </div>
            <div className="text-xs text-green-700">
              {groupedByProvider.length} Provider
              {groupedByProvider.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
        <div className="text-left sm:text-right">
          <div className="text-xs text-green-700">Total Approved</div>
          <div className="text-xl font-bold text-green-900">
            {formatCurrency(grandTotal)}
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block border rounded-md bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Facility</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Expense Date</TableHead>
              <TableHead>Approved Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead>Payout</TableHead>
              <TableHead>Org Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedByProvider.map((group) => (
              <>
                {/* Provider group header */}
                <TableRow
                  key={`provider-${group.provider.id}`}
                  className="bg-slate-50"
                >
                  <TableCell colSpan={9}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">
                        {group.provider.name}
                        <span className="ml-2 text-sm font-normal text-slate-600">
                          ({group.expenses.length} expense
                          {group.expenses.length !== 1 ? "s" : ""})
                        </span>
                      </div>
                      <div className="text-sm font-semibold">
                        Total: {formatCurrency(group.totalAmount)}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>

                {/* Provider's expenses */}
                {group.expenses.map((expense) => (
                  <TableRow key={expense.expenseId}>
                    <TableCell>
                      <div className="text-sm">
                        {expense.providerFirstName} {expense.providerLastName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px]">
                        <div className="text-sm font-medium truncate">
                          {expense.facilityName}
                        </div>
                        {expense.facilityCity && expense.facilityState && (
                          <div className="text-xs text-slate-500">
                            {expense.facilityCity}, {expense.facilityState}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {formatExpenseCategory(expense.category)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {format(
                        parseExpenseDate(expense.expenseDate),
                        "MMM d, yyyy",
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {expense.approvedAt
                        ? format(new Date(expense.approvedAt), "MMM d, yyyy")
                        : "N/A"}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell>
                      {expense.receipts.length === 1 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreviewReceipt(expense.receipts[0])}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {expense.receipts.map((receipt, idx) => (
                            <Button
                              key={`${receipt.path}-${idx}`}
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs justify-start"
                              onClick={() => setPreviewReceipt(receipt)}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              {idx + 1}. {receipt.fileName.substring(0, 15)}...
                            </Button>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <SourcePayoutStatus
                        payoutId={expense.payoutId}
                        payoutStatus={expense.payoutStatus}
                        paidAt={expense.paidAt}
                      />
                    </TableCell>
                    <TableCell>
                      <ExpenseOrgNote
                        expenseId={expense.expenseId}
                        organizationBusinessUnitId={organizationBusinessUnitId}
                        orgNote={expense.orgNote}
                        canEdit={canEditOrgNotes}
                        popover
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-4">
        {groupedByProvider.map((group) => (
          <div
            key={`mobile-provider-${group.provider.id}`}
            className="space-y-2"
          >
            {/* Provider header */}
            <div className="flex items-center justify-between px-1 py-2">
              <div className="font-semibold text-slate-900">
                {group.provider.name}
                <span className="ml-2 text-sm font-normal text-slate-600">
                  ({group.expenses.length})
                </span>
              </div>
              <div className="text-sm font-semibold text-green-700">
                {formatCurrency(group.totalAmount)}
              </div>
            </div>

            {/* Provider's expense cards */}
            {group.expenses.map((expense) => (
              <Card key={expense.expenseId} className="bg-white">
                <CardContent className="p-3 space-y-3">
                  {/* Header row */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">
                        {format(
                          parseExpenseDate(expense.expenseDate),
                          "EEE, MMM d, yyyy",
                        )}
                      </div>
                      <div className="text-sm text-slate-500">
                        {expense.facilityName}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-lg">
                        {formatCurrency(expense.amount)}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {formatExpenseCategory(expense.category)}
                      </Badge>
                    </div>
                  </div>

                  {/* Approval info */}
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span>
                      Approved{" "}
                      {expense.approvedAt
                        ? format(new Date(expense.approvedAt), "MMM d, yyyy")
                        : ""}
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-3 border-t pt-2">
                    <span className="text-xs font-medium text-slate-500">
                      Payout
                    </span>
                    <SourcePayoutStatus
                      payoutId={expense.payoutId}
                      payoutStatus={expense.payoutStatus}
                      paidAt={expense.paidAt}
                    />
                  </div>

                  {/* Receipt section */}
                  {expense.receipts.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
                      {expense.receipts.map((receipt, idx) => (
                        <Button
                          key={`${receipt.path}-${idx}`}
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setPreviewReceipt(receipt)}
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          {expense.receipts.length === 1
                            ? "View Receipt"
                            : `Receipt ${idx + 1}`}
                        </Button>
                      ))}
                    </div>
                  )}

                  {/* Provider note */}
                  {expense.providerNote && (
                    <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                      <span className="text-xs text-slate-500">Note: </span>
                      {expense.providerNote}
                    </div>
                  )}

                  {(canEditOrgNotes || expense.orgNote) && (
                    <div className="border-t pt-2 text-sm">
                      <div className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-500">
                        <FileLock2 className="h-3.5 w-3.5" />
                        Org Note
                      </div>
                      <ExpenseOrgNote
                        expenseId={expense.expenseId}
                        organizationBusinessUnitId={organizationBusinessUnitId}
                        orgNote={expense.orgNote}
                        canEdit={canEditOrgNotes}
                        popover
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ))}
      </div>
      <ExpenseReceiptPreviewDialog
        receipt={previewReceipt}
        onClose={() => setPreviewReceipt(null)}
      />
    </div>
  );
}
